/**
 * Titles and descriptions for the public marketing pages.
 *
 * Imported by both runtimes, so it stays free of anything either one lacks:
 *
 *   - The Worker (src/lib/prerender.ts) writes these into the HTML it serves,
 *     which is what search engines, WhatsApp, iMessage and Slack read. None of
 *     those run the SPA, so a title set only in React is a title they never see.
 *   - The SPA (web/src/App.tsx) sets document.title from the same table on each
 *     navigation, so the tab and the crawled page always agree.
 *
 * Anything not listed here (the dashboard, guest upload, galleries) keeps the
 * plain defaults from web/index.html.
 */

import { COMPARISONS } from "./compare.js";
import { GUIDES } from "./guides.js";

export const SITE_ORIGIN = "https://eventgram.space";
export const SITE_NAME = "Eventgram";
export const DEFAULT_TITLE = "Eventgram";

/** 1200×630, generated by scripts/ops/og-image.py. */
export const OG_IMAGE_PATH = "/og-image.jpg";

export interface PageMeta {
  readonly title: string;
  readonly description: string;
}

export const PAGE_META: Readonly<Record<string, PageMeta>> = {
  "/": {
    title: "Eventgram: Wedding & Event QR Code for Guest Photos, No App",
    description:
      "Guests scan one QR code and every photo and video they take lands in one private album, live on the big screen. No app, no guest login, no subscription. Free to start.",
  },
  "/pricing": {
    title: "Pricing: One-Time Event Passes, Free to Start | Eventgram",
    description:
      "Free for small events. One-time passes for weddings and parties with no subscription, plus yearly plans for photographers, planners and venues.",
  },
  "/demo": {
    title: "Live Demo: See What Your Guests See | Eventgram",
    description:
      "Open a sample wedding album: guest uploads in full quality, a live photo wall for the venue screen, and host moderation. No app, no sign-in.",
  },
  "/privacy": {
    title: "Privacy Policy | Eventgram",
    description: "How Eventgram collects, stores and deletes event photos, videos and account data.",
  },
  "/terms": {
    title: "Terms of Service | Eventgram",
    description: "The terms for hosts and guests using Eventgram event albums.",
  },
  ...Object.fromEntries(
    COMPARISONS.map((c) => [`/${c.slug}`, { title: c.title, description: c.description }]),
  ),
  ...Object.fromEntries(GUIDES.map((g) => [`/${g.slug}`, { title: g.title, description: g.description }])),
};

/** The marketing pages, with any trailing slash removed. */
export function pageMetaFor(pathname: string): PageMeta | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return PAGE_META[path] ?? null;
}
